import React, { useState } from 'react';
import './LoginPage.css';
import Button from '../Components/Buttons';
import SureLoader from '../Components/SureLoader';
import {useAuth} from "../Contexts/AuthContext";
import {UsersApiProvider, useUsersApi} from "../Services/UsersApiProvider";
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { CustomInput, CustomFileUpload } from '../Components/CustomFormElements';


const validateFields = (fields, validationRules, showAlert) => {
    let isValid = true;

    fields.forEach((fieldId) => {
        const field = document.getElementById(fieldId);
        const rules = validationRules[fieldId] || {};

        // Skip if the field doesn't exist
        if (!field) {
            console.warn(`Field with ID '${fieldId}' not found.`);
            return;
        }

        const value = field?.value?.trim() || '';

        // Add focus event listener to clear error class on focus
        field.addEventListener(
            'focus',
            () => {
                field.classList.remove('input-error');
            },
            { once: true }
        );

        // Check required
        if (rules.required && !value) {
            field.classList.add('input-error');
            showAlert('fail', rules.errorMessage || 'This field is required.');
            isValid = false;
            return;
        }

        // Check regex
        if (rules.regex && !rules.regex.test(value)) {
            field.classList.add('input-error');
            showAlert('fail', rules.errorMessage || 'Invalid input.');
            isValid = false;
            return;
        }

        // If passed all checks
        field.classList.remove('input-error');
    });

    return isValid;
};

// ✅ Account Validation Function with API Simulation
const validateAccountFields = async (showAlert, setLoading) => {
    const accountValidationRules = {
        name: {
            required: true,
            regex: /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+$/,
            errorMessage: 'Enter a valid name. No spaces or special characters allowed.',
        },
        'last-name': {
            required: true,
            regex: /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+$/,
            errorMessage: 'Enter a valid last name. No spaces or special characters allowed.',
        },
        email: {
            required: true,
            regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            errorMessage: 'Enter a valid email address.',
        },
        company: {
            required: true,
            regex: /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+(?: [^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+)*$/,
            errorMessage: 'Enter a valid company name. No special characters allowed.',
        },
    };

    const isValid = validateFields(['name', 'last-name', 'email', 'company'], accountValidationRules, showAlert);

    if (!isValid) {
        return false;
    }

    try {
        setLoading(true); // Show loader
        await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate API delay
        showAlert('success', 'Account updated successfully!');
        return true;
    } catch (error) {
        showAlert('fail', 'Failed to update account. Please try again.');
        return false;
    } finally {
        setLoading(false); // Hide loader
    }
};

const validatePasswordFields = async (
    showAlert,
    userId,
    changeUserPassword,
    setLoading,
    password1,
    password2,
    setPassword1,
    setPassword2
) => {
    const passwordValidationRules = {
        password1: {
            required: true,
            regex: /^(?=(?:.*[!@#$%^&*()_+{}\[\]:;<>,.?~]){2,})(?=(?:.*\d){2,}).{10,}$/,
            errorMessage: 'Password must have at least 10 characters, 2 special symbols, and 2 numbers.',
        },
        password2: {
            required: true,
            errorMessage: 'Confirm your password.',
        },
    };

    const isValid = validateFields(
      ['password1', 'password2'],
      passwordValidationRules,
      showAlert,
      { password1, password2 }
    );

    if (isValid && password1 !== password2) {
        showAlert('fail', 'Passwords do not match.');
        return false;
    }

    if (!isValid) return false;

    try {
        setLoading(true); // Show loader
        await changeUserPassword(userId, password1);
        showAlert('success', 'Password updated successfully!');
        setPassword1('');
        setPassword2('');
        return true;
    } catch (error) {
        showAlert('fail', 'Failed to update password. Please try again.');
        return false;
    } finally {
        setLoading(false);
    }
};


// ✅ Sign-Up Page Component
const SuperAdminPageContent = () => {
    const { showAlert } = useAlert();
    const { changeUserPassword } = useUsersApi();
    const { status } = useAuth();

    const userId = status.id
    const [loading, setLoading] = useState(false);

    const [password1, setPassword1] = useState('');
    const [password2, setPassword2] = useState('');

    const handleAccountUpdate = async () => {
        await validateAccountFields(showAlert, setLoading);
    };

    const handleChangePassword = async () => {
        await validatePasswordFields(
            showAlert,
            userId,
            changeUserPassword,
            setLoading,
            password1,
            password2,
            setPassword1,
            setPassword2
        );
    };



    return (
        <div className='container'>
            {loading && <SureLoader fullscreen />}
            <div className="row gap">
            
                <div className="d-flex radius-yes p-2 bg-yes col-12 flex-column gap">
                    
                    <div className='int-col-4 p-2'>
                        <h1 className='h1-title'>Account</h1>
                    </div>

                    <div className='int-col-4 gap d-flex flex-row-to-col'>
                        {/* <div className='int-col-2 p-2 border-yes radius-yes'>
                            <div className='int-col-4 p-2'>
                                <h3 className='h3-title'>General data</h3>
                            </div>
                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Name</div>
                                    <div className='small-font'>Enter superadmin name</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomInput
                                        id="name"
                                        label="Name"
                                        placeholder="Enter name"
                                        icon={<i className="fa-solid fa-user"></i>}
                                        defaultValue=''
                                        type="text"
                                    />
                                </div>
                            </div>

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Last Name</div>
                                    <div className='small-font'>Enter superadmin last name </div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomInput
                                        id="last-name"
                                        label="Last name"
                                        placeholder="Enter last name"
                                        icon={<i className="fa-solid fa-user"></i>}
                                        defaultValue=''
                                        type="text"
                                    />
                                </div>
                            </div>

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Email</div>
                                    <div className='small-font'>Enter superadmin email </div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomInput
                                        id="email"
                                        label="Email"
                                        placeholder="Enter email"
                                        icon={<i className="fa-solid fa-envelope"></i>}
                                        type="text"
                                    />
                                </div>
                            </div>

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Profile image</div>
                                    <div className='small-font'>Upload profile image</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <AlertProvider>
                                        <ConfirmationProvider>
                                            <CustomFileUpload
                                                multiple={false}
                                                maxFileSize={1024 * 1024} // 2 MB
                                                allowedFormats={['image/png', 'image/jpeg']}
                                                showUploadedFiles={true}
                                                allowFileDeletion={true}                        
                                            />
                                        </ConfirmationProvider>
                                    </AlertProvider>
                                </div>
                            </div>

                            <div className='buttons-block w-100 justify-end d-flex flex-row flex-row-to-col'>
                                <Button background="color2" size='large' onClick={handleAccountUpdate}>
                                    Update account
                                </Button>

                            </div>
                        
                        </div> */}




                        <div className='int-col-2 p-2 border-yes radius-yes'>
                            <div className='int-col-4 p-2'>
                                <h2 className='h3-title'>Password</h2>
                            </div>
                            

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Enter password</div>
                                    <div className='small-font'>
                                        Password shold have:
                                        <ul>
                                            <li>min 10 characters</li>
                                            <li>min 2 symbols</li>
                                            <li>min 2 numbers</li>
                                        </ul>
                                    </div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomInput
                                        id="password1"
                                        label="Password"
                                        placeholder="Enter Password"
                                        toggleVisibility={true}
                                        type="password"
                                        value={password1}
                                        onChange={(id, val) => setPassword1(val)}
                                    />
                                </div>
                            </div>

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Confirm password</div>
                                    <div className='small-font'>Enter the same password as above</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomInput
                                        id="password2"
                                        label="Confirm Password"
                                        placeholder="Confirm password"
                                        defaultValue=''
                                        toggleVisibility={true}
                                        type="password"
                                        value={password2}
                                        onChange={(id, val) => setPassword2(val)}
                                    />
                                </div>
                            </div>

                            <div className='buttons-block w-100 justify-end d-flex flex-row flex-row-to-col'>
                                <Button background="color2" size='large' onClick={handleChangePassword}>
                                    Change password
                                </Button>
                                
                            </div>

                        </div>

                    </div>

                </div>
            
            </div>
        </div>
        
        
    );
};

const UserAccount = () => (
    <AlertProvider>
        <ConfirmationProvider>
            <UsersApiProvider>
                <SuperAdminPageContent />
            </UsersApiProvider>
        </ConfirmationProvider>
    </AlertProvider>
);

export default UserAccount;

